/*
 * Central API Error Modal — event bus
 *
 * The <ApiErrorModal/> mounted at the App root listens for a global
 * `chanakya:api-error` CustomEvent and pops a dialog showing the exact
 * server-supplied error message together with a "Copy Error" button.
 *
 * The modal is fired centrally from the React-Query MutationCache
 * onError callback (see /src/index.js) so ANY action mutation
 * (Save, Delete, Confirm, …) that resolves with `success=false`
 * or throws surfaces the same, exact server message in a modal —
 * no per-page audit required.
 *
 * Background list / search APIs use `useQuery` (not mutations) and
 * therefore bypass this handler by design.
 *
 * See PRD Universality Rule #15 — Central Action-API Error Modal.
 */

export const API_ERROR_EVENT = 'chanakya:api-error';

// Suppression window so that page-level `toast.error(err.message)` calls
// firing the same message right after the modal don't stack. See the
// `toast.error` monkey-patch in /src/index.js.
let suppressUntil = 0;
let suppressText = '';

// De-dupe window — when the axios interceptor and the React-Query
// MutationCache both see the same failure, we don't want the modal to
// re-render twice. Consecutive calls with the same message within this
// window are treated as no-ops.
let lastDispatchTs = 0;
let lastDispatchText = '';
const DEDUPE_MS = 250;

/**
 * Display the central Action-API error modal.
 * @param {string} message  exact error text from the server
 * @param {object} [opts]
 * @param {string} [opts.title]  optional dialog title (default: "Action Failed")
 * @param {string} [opts.hint]   optional secondary line (e.g. request name)
 */
export function showApiErrorModal(message, opts = {}) {
  const text = String(message ?? '').trim() || 'The server returned an error.';
  const now  = Date.now();
  if (text === lastDispatchText && (now - lastDispatchTs) < DEDUPE_MS) {
    // Same failure raised twice via different code paths — swallow the
    // duplicate but keep the suppression window fresh so trailing
    // page-level toast.error calls with the same text stay hidden.
    suppressUntil = now + 800;
    suppressText  = text;
    return;
  }
  lastDispatchTs   = now;
  lastDispatchText = text;
  suppressUntil    = now + 800; // ms — enough to swallow the sibling toast
  suppressText     = text;
  window.dispatchEvent(new CustomEvent(API_ERROR_EVENT, {
    detail: {
      message: text,
      title:   opts.title || 'Action Failed',
      hint:    opts.hint  || '',
    },
  }));
}

/** Truthy if a toast carrying `message` should be suppressed because the
 *  modal already displays the same text. */
export function shouldSuppressToast(message) {
  if (Date.now() > suppressUntil) return false;
  const t = String(message ?? '').trim();
  if (!t) return false;
  return t === suppressText;
}

/** Best-effort extraction of the exact server-supplied message from an
 *  Axios / Query error, falling back through the usual layers. */
export function extractServerMessage(err) {
  return (
    err?.serverMessage ||
    err?.response?.data?.message ||
    err?.message ||
    'Unknown error'
  );
}
